import {
  getDailyQuestRowsInRange,
  getProfile,
  listBossQuests,
  listXPHistory,
} from "../api/supabaseData";
import { localToDateString } from "./dateUtils";
import { TITLE_LIST } from "./titleList";
import { getWeekId } from "./bossQuestUtils";
import { getDatePartsInTimeZone } from "./timezoneUtils";

const memoryCache = new Map();
const inFlightCache = new Map();

function uniqueSortedDates(entries = []) {
  return [...new Set(entries.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function diffDays(left, right) {
  const start = new Date(`${left}T00:00:00`);
  const end = new Date(`${right}T00:00:00`);
  const diff = end.getTime() - start.getTime();
  return Math.round(diff / 86400000);
}

// Approximation: streaks are inferred from days with positive net XP events.
function computeLongestPositiveXpStreak(xpHistory = []) {
  const positiveDays = uniqueSortedDates(
    xpHistory.filter((entry) => Number(entry.xp || 0) > 0).map((entry) => entry.date)
  );

  let best = 0;
  let current = 0;
  let previous = null;

  positiveDays.forEach((date) => {
    if (!previous || diffDays(previous, date) === 1) {
      current += 1;
    } else {
      current = 1;
    }
    best = Math.max(best, current);
    previous = date;
  });

  return best;
}

// Approximation: a streak break is counted whenever a positive-XP day follows a gap
// greater than one day inside the last 90 days.
function computeRecentStreakBreaks(xpHistory = [], windowDays = 90) {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - windowDays);
  const cutoffDate = localToDateString(cutoff);

  const positiveDays = uniqueSortedDates(
    xpHistory
      .filter((entry) => Number(entry.xp || 0) > 0 && entry.date >= cutoffDate)
      .map((entry) => entry.date)
  );

  let breaks = 0;
  let previous = null;

  positiveDays.forEach((date) => {
    if (previous && diffDays(previous, date) > 1) {
      breaks += 1;
    }
    previous = date;
  });

  return breaks;
}

function buildRankHistory(level) {
  const currentLevel = Math.max(1, Number(level) || 1);
  return TITLE_LIST.filter((entry) => entry.level <= currentLevel).map(
    (entry) => entry.title
  );
}

function countPerfectDays(rows = []) {
  return rows.reduce((count, row) => {
    const quests = Array.isArray(row.quests) ? row.quests : [];
    if (!quests.length) return count;
    return quests.every((quest) => quest.completed === true || quest.status === "completed")
      ? count + 1
      : count;
  }, 0);
}

function countPenaltyIncidents(rows = []) {
  return rows.reduce((count, row) => {
    const quests = Array.isArray(row.quests) ? row.quests : [];
    return count + quests.filter((quest) => quest.isPenalty).length;
  }, 0);
}

function countBossesAbandoned(bossQuests = [], timeZone = null) {
  const currentWeekId = getWeekId(new Date(), timeZone);
  return bossQuests.filter(
    (quest) =>
      quest.id < currentWeekId &&
      quest.claimed !== true &&
      quest.status !== "completed"
  ).length;
}

export async function buildSystemMemory(uid, options = {}) {
  if (!uid) return null;
  const forceRefresh = !!options.forceRefresh;

  if (!forceRefresh && memoryCache.has(uid)) {
    return memoryCache.get(uid);
  }

  if (!forceRefresh && inFlightCache.has(uid)) {
    return inFlightCache.get(uid);
  }

  const promise = (async () => {
    const [profile, xpHistory, bossQuests] = await Promise.all([
      getProfile(uid),
      listXPHistory(uid),
      listBossQuests(uid),
    ]);

    const firstQuestDate =
      xpHistory.find((entry) => entry?.date)?.date ||
      (profile?.createdAt ? localToDateString(new Date(profile.createdAt)) : null);

    const todayDate = profile?.timeZone
      ? getDatePartsInTimeZone(profile.timeZone, new Date()).date
      : localToDateString(new Date());
    const dailyRows = firstQuestDate
      ? await getDailyQuestRowsInRange(uid, firstQuestDate, todayDate)
      : [];

    const memory = {
      allTimeXP: Number(profile?.totalXP || 0),
      allTimePerfectDays: countPerfectDays(dailyRows),
      allTimePenalties: countPenaltyIncidents(dailyRows),
      longestStreakEver: computeLongestPositiveXpStreak(xpHistory),
      bossesDefeated: bossQuests.filter(
        (quest) => quest.claimed === true || quest.status === "completed"
      ).length,
      bossesAbandoned: countBossesAbandoned(bossQuests, profile?.timeZone || null),
      firstQuestDate,
      rankHistory: buildRankHistory(profile?.level),
      recentStreakBreaks: computeRecentStreakBreaks(xpHistory, 90),
    };

    memoryCache.set(uid, memory);
    inFlightCache.delete(uid);
    return memory;
  })().catch((error) => {
    inFlightCache.delete(uid);
    throw error;
  });

  inFlightCache.set(uid, promise);
  return promise;
}

export function clearSystemMemoryCache(uid = null) {
  if (!uid) {
    memoryCache.clear();
    inFlightCache.clear();
    return;
  }
  memoryCache.delete(uid);
  inFlightCache.delete(uid);
}
